import { PatientProfile } from '@/types/models/patients';

import { normalizeDate, formatDateForInput } from '@/utils/date';

import { fixPhoneNumber } from '@/utils/phone';
import { ISOCalendarDate } from '@/utils/date/types';
import { City, Photo } from '@/types/common';

interface Parent {
    id: number,
    address: string,
    apartment: string,
    birthDate: string,
    city: City,
    email: string,
    firstname: string,
    fullName: string,
    gender: {
        label: string,
        value: string,
    },
    isActive: boolean,
    latitude: number,
    longitude: number,
    patronymic: string,
    phone: string,
    photo: Photo,
    surname: string,
    type: {
        label: string,
        value: string,
    },
}

interface BackendPatientProfile {
    id: number,
    firstname: string,
    surname: string,
    patronymic: string,
    fullName: string,
    isActive: boolean,

    photo?: Photo,
    gender: {
        label: string,
        value: string,
    },

    email?: string,
    new_email?: string,
    phone: string,
    birthDate: ISOCalendarDate,
    parent: Parent,
    type: {
        label: 'Взрослый' | 'Ребенок',
        value: 'adult' | 'child',
    },

    city: City,
    address: string,
    apartment: string,
    latitude?: number,
    longitude?: number,
    fid: string,
}

type PatientProfileRequest = {
    firstname: string,
    surname: string,
    patronymic: string,
    email?: string,
    phone?: string,

    birthDate?: ISOCalendarDate,
    gender?: string,
    parentId?: number,

    address?: string,
    apartment?: string,
    cityId?: number,
    latitude?: number,
    longitude?: number,
    photo?: File,
}


export const transformBackendProfile = (profile: BackendPatientProfile): PatientProfile => {

    const city = profile.city ? {
        id: profile.city.id,
        value: profile.city.name,
    } : null;
    const email = profile.new_email || profile.email;
    const parent = profile.parent ? {
        id: profile.parent.id,
        value: profile.parent.fullName,
    } : null;

    return {
        firstname: profile.firstname,
        surname: profile.surname,
        patronymic: profile.patronymic,
        email: email ?? '',
        phone: profile.phone ? fixPhoneNumber(profile.phone) : '',
        isActive: profile.isActive,

        birth: formatDateForInput(profile.birthDate),
        gender: profile.gender.value,
        type: profile.type.label,
        parent,
        address: profile.address ?? '',
        apartment: profile.apartment ?? '',
        city,
        latitude: profile.latitude ?? '',
        longitude: profile.longitude ?? '',
        photo: profile.photo ?? null,

        fid: profile.fid,
    }
}

export const transformProfile = (profile: PatientProfile): PatientProfileRequest => {

    const birth = normalizeDate(profile.birth);

    const baseFields: {
        firstname: string,
        surname: string,
        patronymic: string,
        email?: string,
        phone?: string,
    } = {
        firstname: profile.firstname,
        surname: profile.surname,
        patronymic: profile.patronymic,
    }

    if (profile.email) {
        baseFields.email = profile.email;
    }

    if (profile.phone) {
        baseFields.phone = profile.phone;
    }

    const additionalFields: Partial<PatientProfileRequest> = {
        birthDate: birth,
        gender: profile.gender,
        address: profile.address,
        apartment: profile.apartment,
    }

    const cityId = profile.city?.id;
    if (cityId) {
        additionalFields.cityId = cityId;
    }

    const parentId = profile.parent?.id;
    if (parentId) {
        additionalFields.parentId = parentId;
    }

    if (profile.latitude) {
        additionalFields.latitude = profile.latitude
    }

    if (profile.longitude) {
        additionalFields.longitude = profile.longitude
    }

    // TODO: import NO_ID const
    if (profile.photo?.id === 0) {
        additionalFields.photo = profile.photo.file
    }

    return {
        ...baseFields,
        ...additionalFields
    }
}
